import { useCallback, useMemo } from 'react';
import { listarDispositivos } from '../../api/dispositivos.js';
import { listarOperadores } from '../../api/operadores.js';
import { TAMANHO_PAGINA_MAXIMO } from '../../api/paginacao.js';
import { useConsulta } from '../../api/useConsulta.js';
import type { OpcaoFiltro } from '../../componentes/Filtros.js';

/** Valor do filtro quando nenhum operador ou dispositivo foi escolhido. */
export const TODOS = 'todos';

/** O valor do seletor vira recorte só quando alguém foi escolhido. */
export function escolhido(valor: string): string | null {
  return valor === TODOS ? null : valor;
}

/**
 * Operadores do tenant para o filtro por autor (RF-072).
 *
 * Uma página só, e grande: o filtro é um seletor, não uma listagem. Falha ao
 * carregar não derruba a tela — mas também não passa em branco: o rótulo do
 * seletor diz que a lista não veio, senão um seletor com uma opção só pareceria
 * um armazém com nenhum operador cadastrado.
 */
export function useOpcoesDeOperador(): readonly OpcaoFiltro[] {
  const buscar = useCallback(() => listarOperadores({ tamanho: TAMANHO_PAGINA_MAXIMO }), []);
  const { dados, erro } = useConsulta(buscar);

  return useMemo(
    () => [
      {
        valor: TODOS,
        rotulo: erro === null ? 'Operador · todos' : 'Operador · lista indisponível',
      },
      ...(dados?.itens ?? []).map((operador) => ({
        valor: operador.id,
        rotulo: `${operador.nome} · ${operador.matricula}`,
      })),
    ],
    [dados, erro],
  );
}

export function useOpcoesDeDispositivo(): readonly OpcaoFiltro[] {
  const buscar = useCallback(() => listarDispositivos({ tamanho: TAMANHO_PAGINA_MAXIMO }), []);
  const { dados, erro } = useConsulta(buscar);

  return useMemo(
    () => [
      {
        valor: TODOS,
        rotulo: erro === null ? 'Dispositivo · todos' : 'Dispositivo · lista indisponível',
      },
      ...(dados?.itens ?? []).map((dispositivo) => ({
        valor: dispositivo.id,
        rotulo: dispositivo.apelido ?? dispositivo.identificador,
      })),
    ],
    [dados, erro],
  );
}
